import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axios';

function ProjectAccessPage() {
  const { hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAccess, setProjectAccess] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Projeleri getir
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/projects');
      setProjects(response.data);
    } catch (err) {
      setError('Projeler yüklenirken hata oluştu: ' + err.message);
    }
  };

  // Kullanıcıları getir
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (err) {
      setError('Kullanıcılar yüklenirken hata oluştu: ' + err.message);
    }
  };

  // Proje erişim yetkilerini getir
  const fetchProjectAccess = async (projectId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/access`);
      setProjectAccess(response.data);
    } catch (err) {
      setError('Proje erişim yetkileri yüklenirken hata oluştu: ' + err.message);
    }
  };

  // Component mount olduğunda projeleri ve kullanıcıları getir
  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  // Proje seçildiğinde erişim yetkilerini getir
  useEffect(() => {
    if (selectedProject) {
      fetchProjectAccess(selectedProject.id);
    }
  }, [selectedProject]);

  // Erişim yetkisi verme
  const handleAddAccess = async () => {
    try {
      await axios.post(`/projects/${selectedProject.id}/access`, {
        userId: selectedUser
      });
      setSuccess(true);
      setAddDialogOpen(false);
      setSelectedUser('');
      fetchProjectAccess(selectedProject.id);
    } catch (err) {
      setError('Erişim yetkisi verilirken hata oluştu: ' + err.message);
    }
  };

  // Erişim yetkisi kaldırma
  const handleRemoveAccess = async (userId) => {
    try {
      await axios.delete(`/projects/${selectedProject.id}/access/${userId}`);
      setSuccess(true);
      fetchProjectAccess(selectedProject.id);
    } catch (err) {
      setError('Erişim yetkisi kaldırılırken hata oluştu: ' + err.message);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Proje Erişim Yetkileri
        </Typography>

        {/* Proje Seçimi */}
        <FormControl fullWidth sx={{ mb: 4 }}>
          <InputLabel>Proje Seçin</InputLabel>
          <Select
            value={selectedProject?.id || ''}
            onChange={(e) => {
              const project = projects.find(p => p.id === e.target.value);
              setSelectedProject(project);
            }}
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedProject && (
          <>
            {/* Yetki Listesi */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Yetkili Kullanıcılar
              </Typography>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Yetki Ver
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kullanıcı Adı</TableCell>
                    <TableCell>Ad Soyad</TableCell>
                    <TableCell>E-posta</TableCell>
                    <TableCell>Yetki Verilme Tarihi</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectAccess.map((access) => (
                    <TableRow key={access.userId}>
                      <TableCell>{access.username}</TableCell>
                      <TableCell>{access.fullName}</TableCell>
                      <TableCell>{access.email}</TableCell>
                      <TableCell>
                        {new Date(access.createDate).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Yetkiyi Kaldır">
                          <IconButton
                            onClick={() => handleRemoveAccess(access.userId)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {projectAccess.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Henüz hiçbir kullanıcıya yetki verilmemiş
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Yetki Verme Dialog'u */}
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Erişim Yetkisi Ver</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Kullanıcı</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                {users
                  .filter(user => !projectAccess.some(access => access.userId === user.id))
                  .map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.fullName} ({user.username})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={handleAddAccess}
              variant="contained"
              disabled={!selectedUser}
            >
              Yetki Ver
            </Button>
          </DialogActions>
        </Dialog>

        {/* Hata Mesajı */}
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Başarı Mesajı */}
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            İşlem başarıyla tamamlandı
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default ProjectAccessPage; 